import { defineStore } from "pinia";
import { ALL_50_STATES } from "@/constants";
import { isValidPhoneNumber } from "libphonenumber-js";

import { useAuthStore, useCrudStore, useDataStore } from "@/stores";
import { data } from "autoprefixer";

export const useSchemaStore = defineStore("schemaStore", {
  state: () => ({
    // Schema properties
    all_contact_schema: [],
    all_account_schema: [],
    all_banking_schema: [],
    all_distribution_schema: [],
    all_investment_schema: [],
    all_document_schema: [],
    all_offerings: [],
    all_trusted_contact_schema: [],
    custodian_name_options: [],
    admin_investment_types: [],
    disabled_beneficial_owner_contact_fields: [],
    disabled_joint_contact_fields: [],
    disabled_owner_contact_fields: [],
    disabled_entity_account_fields: [],
    rollover_table_fields: [],
    title_transfer_reasons: [],
    title_transfer_schema: [],
    transfer_banner_headers: [],
    // Schema templates
    accredited_owner_contact_personal_info_schema: [],
    non_accredited_owner_contact_personal_info_schema: [],
    accredited_joint_contact_personal_info_schema: [],
    non_accredited_joint_contact_personal_info_schema: [],
    accredited_ben_owner_contact_personal_info_schema: [],
    non_accredited_ben_owner_contact_personal_info_schema: [],
    entity_information: [],
    suitability_questions: [],
    modal_banking_schema: [],
    funding_account_template: {},
    distribution_account_template: {},
    // Investment types
    investment_types: [],
    schema_store_is_set: false,
    show_input_errors: false,
    read_only_after_investments: ["ssn", "ein", "first_name", "last_name", "name", "email"],
  }),
  getters: {},
  actions: {
    async setSchema() {
      const crudStore = useCrudStore();

      //TODO: Somewhere in here set is_secure_field, place_holder, and additional_info on the schema records

      try {
        // Fetch the configuration lists based on the references

        let configurationLists = await crudStore.find("settings", [
          { field_name: "is_list", operator: "eq", value: true },
        ]);

        this.admin_investment_types = configurationLists.find(
          (config) => config.reference === "admin_investment_types"
        ).options;
        this.disabled_beneficial_owner_contact_fields = configurationLists.find(
          (config) => config.reference === "disabled_beneficial_owner_contact_fields"
        ).options;
        this.disabled_joint_contact_fields = configurationLists.find(
          (config) => config.reference === "disabled_joint_contact_fields"
        ).options;
        this.disabled_owner_contact_fields = configurationLists.find(
          (config) => config.reference === "disabled_owner_contact_fields"
        ).options;
        this.disabled_entity_account_fields = configurationLists.find(
          (config) => config.reference === "disabled_entity_account_fields"
        ).options;
        this.rollover_table_fields = configurationLists.find(
          (config) => config.reference === "rollover_table_fields"
        ).options;
        this.title_transfer_reasons = configurationLists.find(
          (config) => config.reference === "title_transfer_reasons"
        ).options;
        let transfer_banner_headers = configurationLists.find(
          (config) => config.reference === "transfer_banner_headers"
        ).options;

        // Fetch schemas and custodian names concurrently
        let allSchema = await crudStore.find("schema", []); // or pass [] if your API requires filters

        for (let schema of allSchema) {
          if (["ssn", "ein"].includes(schema.field_name)) {
            schema.is_secure_field = true;
          }
          // iff ssn make schema.max_length 11
          if (schema.field_name === "ssn") {
            schema.max_length = 11;
          }
          if (schema.field_name === "ein") {
            schema.max_length = 10;
          }
          //if phone number make schema.max_length 14
          if (schema.field_name === "phone") {
            schema.max_length = 14;
          }
        }

        // keep the same variable names
        const contactSchema = allSchema.filter((s) => s.collection_name === "contacts");
        const accountSchema = allSchema.filter((s) => s.collection_name === "accounts");
        const bankingSchema = allSchema.filter((s) => s.collection_name === "bankaccounts");
        const investmentSchema = allSchema.filter((s) => s.collection_name === "investments");
        const distributionSchema = allSchema.filter((s) => s.collection_name === "distributions");
        const trustedContactSchema = allSchema.filter(
          (s) => s.collection_name === "trustedcontacts"
        );
        const documentSchema = allSchema.filter((s) => s.collection_name === "documents");

        // console.log("documentSchema:", documentSchema);

        // Assign fetched schemas and custodian names to the store properties
        this.all_contact_schema = contactSchema;
        this.all_account_schema = accountSchema;
        this.all_banking_schema = bankingSchema;
        this.all_investment_schema = investmentSchema;
        //this.all_offerings = offerings;
        this.all_distribution_schema = distributionSchema;
        this.all_trusted_contact_schema = trustedContactSchema;
        this.all_document_schema = documentSchema;

        //this.custodian_name_options = custodianNames[0]?.names || [];
        //this.all_custodians = allCustodians;

        // Helper function to map configuration to schema fields
        const mapConfigToSchema = (config, flattenedSchema, useAccreditedFields) =>
          config.options.map((groupName) => ({
            group_name: groupName,
            fields: flattenedSchema.filter(
              (field) =>
                field.record_detail_group === groupName &&
                (useAccreditedFields || !field.accredited_only)
            ),
          }));

        // Flatten the schemas based on different configurations
        const flattenedOwnerContactSchema = this.flattenSchema(contactSchema, "default");
        const flattenedJointContactSchema = this.flattenSchema(contactSchema, "joint");
        const flattenedBenOwnerSchema = this.flattenSchema(contactSchema, "beneficial_owner");
        const flattenedBankingSchema = this.flattenSchema(bankingSchema, "default");
        const flattenedEntitySchema = this.flattenSchema(accountSchema, "entity");
        // const flattenedSuitabilitySchema = this.flattenSchema(suitabilitySchema, "default");

        this.modal_banking_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "bank_account_fields"),
          flattenedBankingSchema,
          true // Use accredited fields
        );

        // Map configurations to schemas
        this.accredited_owner_contact_personal_info_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "accredited_personal_info"),
          flattenedOwnerContactSchema,
          true // Use accredited fields
        );

        this.non_accredited_owner_contact_personal_info_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "non_accredited_personal_info"),
          flattenedOwnerContactSchema,
          false // Do not use accredited fields
        );

        this.accredited_joint_contact_personal_info_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "accredited_joint_info"),
          flattenedJointContactSchema,
          true // Use accredited fields
        );

        this.non_accredited_joint_contact_personal_info_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "non_accredited_joint_info"),
          flattenedJointContactSchema,
          false // Do not use accredited fields
        );

        this.accredited_ben_owner_contact_personal_info_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "accredited_ben_owner_info"),
          flattenedBenOwnerSchema,
          true // Use accredited fields
        );

        this.non_accredited_ben_owner_contact_personal_info_schema = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "non_accredited_ben_owner_info"),
          flattenedBenOwnerSchema,
          false // Do not use accredited fields
        );

        this.entity_information = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "entity_information"),
          flattenedEntitySchema,
          true // Use accredited fields
        );

        this.suitability_questions = mapConfigToSchema(
          configurationLists.find((config) => config.reference === "suitability_info"),
          flattenedOwnerContactSchema,
          true // Use accredited fields
        );

        this.schema_store_is_set = true;
      } catch (err) {
        console.error(err);
      }
    },
    flattenSchema(schema, config = "default") {
      // Flatten the schema
      const flattened = schema.map((item) => {
        // Destructure the item to separate record_detail_config and the rest of the fields
        const { record_detail_config, ...rest } = item;

        // Extract the required config or default if not provided
        const selectedConfig = record_detail_config[config] || record_detail_config["default"];

        // Merge the selected config fields into the rest of the item fields
        return {
          ...rest,
          ...selectedConfig,
        };
      });

      // Sort the flattened array based on record_detail_group_order
      flattened.sort((a, b) => {
        const orderA = parseInt(a.record_detail_group_order) || 0;
        const orderB = parseInt(b.record_detail_group_order) || 0;
        return orderA - orderB;
      });

      // Return the sorted array
      return flattened;
    },
    addCreatedUpdatedFields(record, add_created, add_updated) {
      const dataStore = useDataStore();

      let name = `${dataStore.contact.first_name} ${dataStore.contact.last_name}`;
      let user_id = dataStore.contact.user_id;

      if (add_updated) {
        record.updated_date = new Date();
        record.updated_by_id = user_id;
        record.updated_by_name = name;
      }
      if (add_created) {
        record.created_by_name = name;
        record.created_by_id = user_id;
        record.created_date = new Date();
      }

      return record;
    },
    getSchemaByCollection(collection) {
      switch (collection) {
        case "Contacts":
        case "contacts":
          return this.all_contact_schema;
        case "Accounts":
        case "accounts":
          return this.all_account_schema;
        case "Investments":
        case "investments":
        case "tempinvestments":
          return this.all_investment_schema;
        case "BankAccounts":
        case "bankaccounts":
          return this.all_banking_schema;
        case "Documents":
        case "documents":
        case "tempdocuments":
          return this.all_document_schema;
        default:
          return [];
      }
    },
    getTableHeadersForCollection(collection) {
      // Get the schema for the specified collection
      let schema = this.getSchemaByCollection(collection);

      // Filter schema to include only items where show_in_search_results is true
      let filteredSchema = schema.filter((item) => item.show_in_search_results);

      // Sort the filtered schema by search_result_order
      let sortedSchema = filteredSchema.sort(
        (a, b) => a.search_result_order - b.search_result_order
      );

      // Map the sorted schema to match the format expected from the aggregation
      let tableHeaders = sortedSchema.map((item) => ({
        label: item.label,
        field_name: item.field_name,
        associated_field_name: item.associated_field_name,
        is_association_field: item.is_association_field,
        search_result_order: item.search_result_order,
        field_type: item.field_type,
        number_type: item.number_type,
        value: "", // Default value as per the original aggregation
      }));

      return tableHeaders;
    },
    formatRecords(records, collection, direction) {
      let schema = this.getSchemaByCollection(collection);

      if (!schema || schema.length === 0) {
        return records;
      }

      // Get the list of valid field names from schema
      const validFields = schema.map((item) => item.field_name);

      for (let record of records) {
        for (let schema_item of schema) {
          if (
            schema_item.field_type === "number" &&
            schema_item.number_type === "currency" &&
            record[schema_item.field_name] &&
            typeof record[schema_item.field_name] === "string"
          ) {
            let numericString = record[schema_item.field_name].replace(/,/g, "");
            let numericValue = parseFloat(numericString);
            record[schema_item.field_name] = numericValue;
          }

          if (
            ["ssn", "ein", "account_number", "routing_number"].includes(schema_item.field_name) &&
            record[schema_item.field_name]
          ) {
            // console.log("processing field that is b64 encodes:", schema_item.field_name);
            if (direction == "inbound") {
              // console.log("original_value:", record[schema_item.field_name]);
              record[schema_item.field_name] = atob(record[schema_item.field_name]);
              // console.log("decoded_value:", record[schema_item.field_name]);
            }
            if (direction == "outbound") {
              // console.log("original_value:", record[schema_item.field_name]);
              record[schema_item.field_name] = btoa(record[schema_item.field_name]);
              // console.log("encoded_value:", record[schema_item.field_name]);
            }
          }
          if (
            ["state", "ma_state"].includes(schema_item.field_name) &&
            record[schema_item.field_name]
          ) {
            if (direction == "inbound") {
              // console.log("Before state inbound:", record[schema_item.field_name]);
              record[schema_item.field_name] = this.states(
                record[schema_item.field_name],
                "inbound"
              );
              // console.log("After state inbound:", record[schema_item.field_name]);
            }
            if (direction == "outbound") {
              record[schema_item.field_name] = this.states(
                record[schema_item.field_name],
                "outbound"
              );
            }
          }
          // if field_type is date format date for base input coming in and reformat going out
          if (
            schema_item.field_type === "date" &&
            schema_item.belongs_to_nested_object &&
            record[schema_item.nested_object_name]
          ) {
            // if (direction == "inbound") {
            //   if (schema_item.belongs_to_nested_object) {
            const nestedValue = record[schema_item.nested_object_name]?.[schema_item.field_name];
            if (nestedValue) {
              record[schema_item.nested_object_name][schema_item.field_name] = new Date(
                nestedValue
              );
            } else {
              record[schema_item.nested_object_name][schema_item.field_name] = null;
            }

            delete record[schema_item.field_name];
            //This will prevent duplication of date fields at top-level
          }
        }

        // Strip fields not in schema
        for (let key of Object.keys(record)) {
          if (!validFields.includes(key)) {
            delete record[key];
          }
        }

        // if
      }

      if (direction === "outbound") {
        // console.log("Formatted records for insert/update:", records);
      }

      return records;
    },
    states(state, direction) {
      const US_STATES = [
        { abbr: "AL", full: "Alabama" },
        { abbr: "AK", full: "Alaska" },
        { abbr: "AZ", full: "Arizona" },
        { abbr: "AR", full: "Arkansas" },
        { abbr: "CA", full: "California" },
        { abbr: "CO", full: "Colorado" },
        { abbr: "CT", full: "Connecticut" },
        { abbr: "DE", full: "Delaware" },
        { abbr: "DC", full: "District of Columbia" },
        { abbr: "FL", full: "Florida" },
        { abbr: "GA", full: "Georgia" },
        { abbr: "HI", full: "Hawaii" },
        { abbr: "ID", full: "Idaho" },
        { abbr: "IL", full: "Illinois" },
        { abbr: "IN", full: "Indiana" },
        { abbr: "IA", full: "Iowa" },
        { abbr: "KS", full: "Kansas" },
        { abbr: "KY", full: "Kentucky" },
        { abbr: "LA", full: "Louisiana" },
        { abbr: "ME", full: "Maine" },
        { abbr: "MD", full: "Maryland" },
        { abbr: "MA", full: "Massachusetts" },
        { abbr: "MI", full: "Michigan" },
        { abbr: "MN", full: "Minnesota" },
        { abbr: "MS", full: "Mississippi" },
        { abbr: "MO", full: "Missouri" },
        { abbr: "MT", full: "Montana" },
        { abbr: "NE", full: "Nebraska" },
        { abbr: "NV", full: "Nevada" },
        { abbr: "NH", full: "New Hampshire" },
        { abbr: "NJ", full: "New Jersey" },
        { abbr: "NM", full: "New Mexico" },
        { abbr: "NY", full: "New York" },
        { abbr: "NC", full: "North Carolina" },
        { abbr: "ND", full: "North Dakota" },
        { abbr: "OH", full: "Ohio" },
        { abbr: "OK", full: "Oklahoma" },
        { abbr: "OR", full: "Oregon" },
        { abbr: "PA", full: "Pennsylvania" },
        { abbr: "PR", full: "Puerto Rico" },
        { abbr: "RI", full: "Rhode Island" },
        { abbr: "SC", full: "South Carolina" },
        { abbr: "SD", full: "South Dakota" },
        { abbr: "TN", full: "Tennessee" },
        { abbr: "TX", full: "Texas" },
        { abbr: "UT", full: "Utah" },
        { abbr: "VT", full: "Vermont" },
        { abbr: "VA", full: "Virginia" },
        { abbr: "WA", full: "Washington" },
        { abbr: "WV", full: "West Virginia" },
        { abbr: "WI", full: "Wisconsin" },
        { abbr: "WY", full: "Wyoming" },
      ];
      if (direction === "inbound") {
        const stateObj = US_STATES.find((s) => s.abbr?.toLowerCase() === state?.toLowerCase());
        return stateObj ? stateObj.full : state;
      } else if (direction === "outbound") {
        const stateObj = US_STATES.find((s) => s.full?.toLowerCase() === state?.toLowerCase());

        return stateObj ? stateObj.abbr : state;
      }
      return state;
    },
    validateFields(record, schema, record_id, turn_on_show_errors, object_type, original_record) {
      const dataStore = useDataStore();

      let is_valid = true;
      const invalid_fields = [];
      const invalid_groups = [];

      // 1 error message per field_name (first failure wins)
      const field_errors = {}; // { [field_name]: "message" }

      const pushInvalid = (field) => {
        const label = field.label || field.field_name;
        if (!invalid_fields.includes(label)) invalid_fields.push(label);

        const group_name = field.record_detail_group || "";
        if (!invalid_groups.includes(group_name)) invalid_groups.push(group_name);
      };

      const setFieldError = (field, message) => {
        // only one error per field_name
        if (field_errors[field.field_name]) return;

        field_errors[field.field_name] = message;
        is_valid = false;
        pushInvalid(field);
      };

      const hasValue = (v) => !(v === null || v === undefined || v === "");

      const getFieldValue = (field) => {
        if (field.belongs_to_nested_object) {
          return record?.[field.nested_object_name]?.[field.field_name];
        }
        return record?.[field.field_name];
      };

      const getOriginalFieldValue = (field) => {
        if (field.belongs_to_nested_object) {
          return original_record?.[field.nested_object_name]?.[field.field_name];
        }
        return original_record?.[field.field_name];
      };

      const setFieldValue = (field, val) => {
        if (field.belongs_to_nested_object) {
          if (!record[field.nested_object_name]) record[field.nested_object_name] = {};
          record[field.nested_object_name][field.field_name] = val;
        } else {
          record[field.field_name] = val;
        }
      };

      // trims ends and collapses repeated internal whitespace (e.g. "Mary  Ann" -> "Mary Ann")
      // so accidental double spaces don't get rejected by the name regex below
      const normalizeNameValue = (v) =>
        typeof v === "string" ? v.trim().replace(/\s+/g, " ") : v;

      // ----- helpers: compare date-only (ignores time) -----
      const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

      const parseDateOnly = (val) => {
        const d = new Date(val);
        if (Number.isNaN(d.getTime())) return null;
        return startOfDay(d);
      };

      const today = startOfDay(new Date());

      // Build uniqueness maps (excluding the current record_id when editing)
      const contacts_to_ssns = {};
      const contacts_to_emails = {};
      const accounts_to_eins = {};

      const addContactToMaps = (c) => {
        if (!c?.record_id) return;
        contacts_to_ssns[c.record_id] = c.ssn;
        contacts_to_emails[c.record_id] = c.email;
      };

      addContactToMaps(dataStore.contact);
      (dataStore.joint_contacts || []).forEach(addContactToMaps);
      (dataStore.beneficial_owner_contacts || []).forEach(addContactToMaps);

      (dataStore.entity_accounts || []).forEach((a) => {
        if (!a?.record_id) return;
        accounts_to_eins[a.record_id] = a.ein;
      });

      if (record_id) {
        delete contacts_to_ssns[record_id];
        delete contacts_to_emails[record_id];
        delete accounts_to_eins[record_id];
      }

      const ssnSet = new Set(Object.values(contacts_to_ssns).filter(hasValue));
      const emailSet = new Set(Object.values(contacts_to_emails).filter(hasValue));
      const einSet = new Set(Object.values(accounts_to_eins).filter(hasValue));

      // Validators (return null if valid, otherwise an error message)
      const validators = {
        // Regex enforces the exact (XXX) XXX-XXXX shape other code hard-depends on
        // (e.g. PersonalDetails.vue's phone.length === 14 check); isValidPhoneNumber
        // enforces it's a real NANP number. Keep both — isValidPhoneNumber alone
        // accepts other formats (raw digits, +1E.164, etc.) that would break those.
        phone: (v) =>
          /^\(\d{3}\) \d{3}-\d{4}$/.test(v) && isValidPhoneNumber(v, "US")
            ? null
            : `Please enter a valid phone number.`,

        ssn: (v) =>
          /^\d{3}-\d{2}-\d{4}$/.test(v) ? null : `Please enter a valid SSN (format: 123-45-6789).`,

        ein: (v) =>
          /^\d{2}-\d{7}$/.test(v) ? null : `Please enter a valid EIN (format: 12-3456789).`,

        email: (v) =>
          // from your validateInputValue snippet
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)
            ? null
            : `Please enter a valid email address.`,

        amount: (v) => (/^\d{1,3}(,\d{3})*$/.test(v) ? null : `Please enter a valid amount.`),

        first_name: (v) =>
          /^[a-zA-Z]+([.'-\s][a-zA-Z]+)*\.?$/.test(v) ? null : `Please enter a valid first name.`,

        last_name: (v) =>
          /^[a-zA-Z]+([.'-\s][a-zA-Z]+)*\.?$/.test(v) ? null : `Please enter a valid last name.`,

        zip_code: (v) => (/^\d{5}(-\d{4})?$/.test(v) ? null : `Please enter a valid ZIP code.`),

        ma_zip_code: (v) => (/^\d{5}(-\d{4})?$/.test(v) ? null : `Please enter a valid ZIP code.`),

        state: (v) => {
          // Prefer your ALL_50_STATES structure; also accept abbr
          const found = (ALL_50_STATES || []).find((obj) => obj.name === v || obj.abbr === v);
          return found ? null : `Please enter a valid state.`;
        },

        ma_state: (v) => {
          const found = (ALL_50_STATES || []).find((obj) => obj.name === v || obj.abbr === v);
          return found ? null : `Please enter a valid state.`;
        },

        date_of_birth: (v) => {
          const dobRaw = new Date(v);
          const todayRaw = new Date();

          if (Number.isNaN(dobRaw.getTime()) || dobRaw.getFullYear() <= 1900) {
            return `Please enter a valid date of birth (year must be after 1900).`;
          }

          let age = todayRaw.getFullYear() - dobRaw.getFullYear();
          const monthDiff = todayRaw.getMonth() - dobRaw.getMonth();
          const dayDiff = todayRaw.getDate() - dobRaw.getDate();
          if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;

          if (age < 18)
            return `Please enter a valid date of birth (must be at least 18 years old).`;
          if (dobRaw > todayRaw) return `Date of birth cannot be in the future.`;

          return null;
        },

        id_issued_date: (v) => {
          const parsed = parseDateOnly(v);
          if (!parsed || parsed.getFullYear() <= 1900)
            return `Please enter a valid ID issued date.`;
          // align with your validateInputValue: today or earlier
          if (parsed > today) return `ID issued date must be today or in the past.`;
          return null;
        },

        id_expiration_date: (v) => {
          const parsed = parseDateOnly(v);
          if (!parsed || parsed.getFullYear() <= 1900)
            return `Please enter a valid ID expiration date.`;
          if (parsed <= today) return `ID expiration date must be in the future.`;
          return null;
        },
      };

      // Run validation per field
      for (const field of schema) {
        let value = getFieldValue(field);

        if (["first_name", "last_name"].includes(field.field_name)) {
          const normalized = normalizeNameValue(value);
          if (normalized !== value) {
            setFieldValue(field, normalized);
            value = normalized;
          }
        }

        // REQUIRED (handles nested too)
        if (field.is_required && !hasValue(value)) {
          setFieldError(field, `Please enter a value for ${field.label || field.field_name}.`);
          continue; // don't stack more errors for this field
        }

        // FORMAT + RULE VALIDATIONS (only if value exists)
        if (hasValue(value)) {
          // grandfather pre-existing phone numbers that already failed stricter
          // validation before this field was touched, so unrelated edits on the
          // same form aren't blocked by legacy bad data
          const is_unchanged_legacy_phone =
            field.field_name === "phone" &&
            original_record !== undefined &&
            value === getOriginalFieldValue(field);

          // specific field_name validators
          if (validators[field.field_name] && !is_unchanged_legacy_phone) {
            const msg = validators[field.field_name](value);
            if (msg) {
              setFieldError(field, msg);
              continue;
            }
          }

          // generic date type sanity (if not already handled above)
          if (
            field.field_type === "date" &&
            !["date_of_birth", "id_issued_date", "id_expiration_date"].includes(field.field_name)
          ) {
            const parsed = parseDateOnly(value);
            if (!parsed || parsed.getFullYear() <= 1900) {
              setFieldError(field, `Please enter a valid ${field.label || field.field_name}.`);
              continue;
            }
          }
        }

        // UNIQUENESS (only if still no error for this field)
        if (!field_errors[field.field_name] && hasValue(value)) {
          if (field.field_name === "ssn" && ssnSet.has(value)) {
            setFieldError(field, `The SSN is already in use.`);
          } else if (field.field_name === "ein" && einSet.has(value)) {
            setFieldError(field, `The EIN is already in use.`);
          } else if (
            field.field_name === "email" &&
            emailSet.has(value) &&
            object_type !== "trusted_contact"
          ) {
            setFieldError(field, `The email ${value} is already in use.`);
          }
        }
      }

      if (turn_on_show_errors) {
        this.show_input_errors = !is_valid;
        //console.log(is_valid ? "Record validation passed" : "Record validation failed");
      }

      // Keep your existing return shape, but now guaranteed 1 message per field_name
      const error_messages = Object.entries(field_errors).map(([field_name, message]) => ({
        field_name,
        message,
      }));

      return {
        proceed: is_valid,
        invalid_fields,
        invalid_groups,
        error_messages, // [{ field_name, message }]
        field_errors, // optional convenience map
      };
    },
  },
});
